/**
 * ServiceGenerationView — Service Generation workbench mode
 *
 * Phase 3 of the Code Resolution Workbench rollout.
 * Thin wrapper: WorkbenchShell chrome + CodeEditorView (hideHeader=true) as center content.
 *
 * Mode identity:
 *   This is the "post-generation review" stage. The user is reviewing Java services
 *   and entities generated by the Service Generation node. They can inspect the output,
 *   make targeted edits, and save changes before the output moves downstream.
 *
 * Shell slots owned by this mode:
 *   headerIcon    — Layers (service decomposition identity)
 *   title         — "Service Generation"
 *   contextTag    — "ClassName · Post-Generation" (location, not a repeat of title)
 *   headerStatus  — "Generated Output" stage badge
 *   statusBar     — stage framing strip: what was produced, what the user is reviewing
 *   headerActions — stage-aware actions: Export COBOL, Export Java, Revert File, AI Format, Save
 *   centerContent — CodeEditorView (hideHeader=true): Sections | COBOL | Generated Services (Java)
 *
 * CodeEditorView triggers (all ref-stable via handleXxxRef pattern):
 *   editorRef.current.triggerDownloadSource()  — export COBOL source
 *   editorRef.current.triggerDownloadTarget()  — export generated Java
 *   editorRef.current.triggerStartFresh()      — revert file to generated original
 *   editorRef.current.triggerCleanCode()       — AI format generated code
 *   editorRef.current.triggerSave()            — persist edits
 *
 * CSS: ServiceGenerationView.css  (sgv-* prefix, mode-specific styles only)
 * Shell CSS: WorkbenchShell.css   (rv-* prefix, shared chrome)
 */

import { useRef } from 'react'
import { Layers, Download, RotateCcw, Wand2, Save, Cpu } from 'lucide-react'
import WorkbenchShell from '../workbench/WorkbenchShell'
import CodeEditorView from './CodeEditorView'
import './ServiceGenerationView.css'

export default function ServiceGenerationView({
  onBack,
  analysisContext,
  nodeType,
  source = 'service-generation',
}) {
  const editorRef = useRef(null)

  // ── Header icon — Layers represents service decomposition ──────────────────
  const wbHeaderIcon = (
    <Layers className="rv-header-icon" size={15} />
  )

  // ── Context tag — location context, not a repeat of the title ─────────────
  // "ClassName · Post-Generation" tells the user: what application, what stage
  const wbContextTag = analysisContext?.className
    ? `${analysisContext.className} · Post-Generation`
    : 'Post-Generation'

  // ── Header status — stage identity badge ──────────────────────────────────
  // Small pill that clearly marks this as a generated-output review screen
  const wbHeaderStatus = (
    <span className="sgv-stage-badge">
      <Cpu size={10} />
      Generated Output
    </span>
  )

  // ── Status bar — stage framing strip ──────────────────────────────────────
  // The most important identity cue: tells the user exactly what they're reviewing
  // and what the expected workflow is at this stage.
  const wbStatusBar = (
    <div className="rv-status-bar rv-status-bar--idle sgv-status-bar">
      <Layers size={11} className="sgv-sb-icon" />
      <span className="rv-sb-text">Reviewing generated Java services and entities</span>
      <span className="rv-sb-sep">·</span>
      <span className="sgv-sb-stage">Service Generation output</span>
      <span className="rv-sb-sep">·</span>
      <span className="sgv-sb-hint">Inspect · edit if needed · save to persist changes downstream</span>
    </div>
  )

  // ── Header actions — stage-aware labels ───────────────────────────────────
  // Labels communicate what the action means in this context, not just the file type
  const wbHeaderActions = (
    <div className="sgv-header-actions">

      {/* Export group — get the output files */}
      <button
        className="sgv-action-btn sgv-action-btn--export"
        onClick={() => editorRef.current?.triggerDownloadSource()}
        title="Export original COBOL source file"
      >
        <Download size={13} />
        <span>Export COBOL</span>
      </button>
      <button
        className="sgv-action-btn sgv-action-btn--export"
        onClick={() => editorRef.current?.triggerDownloadTarget()}
        title="Export generated Java output file"
      >
        <Download size={13} />
        <span>Export Java</span>
      </button>

      <div className="sgv-action-divider" />

      {/* Edit group — modify the generated output */}
      <button
        className="sgv-action-btn sgv-action-btn--danger"
        onClick={() => editorRef.current?.triggerStartFresh()}
        title="Revert this file to the original generated version"
      >
        <RotateCcw size={13} />
        <span>Revert File</span>
      </button>
      <button
        className="sgv-action-btn"
        onClick={() => editorRef.current?.triggerCleanCode()}
        title="AI Format — clean up generated code style and formatting"
      >
        <Wand2 size={13} />
        <span>AI Format</span>
      </button>

      <div className="sgv-action-divider" />

      {/* Persist group */}
      <button
        className="sgv-action-btn sgv-action-btn--primary"
        onClick={() => editorRef.current?.triggerSave()}
        title="Save edits to this generated Java file"
      >
        <Save size={13} />
        <span>Save</span>
      </button>
    </div>
  )

  // ── Center content: CodeEditorView with header suppressed ──────────────────
  // hideHeader=true: WorkbenchShell owns the chrome; CodeEditorView owns the editors.
  // The inner SectionNavigator, COBOL panel, and Java panel render as normal.
  // Java panel title is "Generated Services (Java)" for source='service-generation'
  // (handled in CodeEditorView line ~1379 via isServiceGenSource check).
  const wbCenterContent = (
    <CodeEditorView
      ref={editorRef}
      source={source}
      nodeType={nodeType}
      analysisContext={analysisContext}
      onBack={onBack}
      hideHeader={true}
    />
  )

  return (
    <WorkbenchShell
      onBack={onBack}
      backLabel="Back to Workflow"
      headerIcon={wbHeaderIcon}
      title="Service Generation"
      contextTag={wbContextTag}
      headerStatus={wbHeaderStatus}
      headerActions={wbHeaderActions}
      statusBar={wbStatusBar}
      // hideSidebar: CodeEditorView owns its internal layout (SectionNavigator + COBOL + Java).
      // Using hideSidebar=true bypasses the shell Allotment entirely — no icon rail,
      // no collapsed stub — centerContent gets 100% of the body width.
      hideSidebar={true}
      centerContent={wbCenterContent}
      centerMinSize={600}
    />
  )
}
